import React from 'react';
import {Typography} from '@mui/material';

/** Keys aligned with PropertyPlantEquipmentWpEnum ROW names. */
export const PPE_TITLE_KEYS = [
    'LOCATION_OF_PROPERTY_ROW',
    'COMMERCIAL_ALLOWANCE_ROW',
    'INDUSTRIAL_ALLOWANCE_ROW',
    'FURNITURE_FIXTURE_ROW',
    'COMPUTER_HARD_SOFT_ROW',
    'FULL_PAY_MOTOR_ROW',
    'HIRE_PURCHASE_MOTOR_ROW',
    'MACHINERY_ROW',
    'LIFT_ESCALATOR_ROW',
    'EQUIPMENT_ROW',
] as const;

export type PpeTitleKey = (typeof PPE_TITLE_KEYS)[number];

/** Title enum key → WP payload array property (camelCase). */
export const PPE_TITLE_KEY_TO_ROW_KEY: Record<PpeTitleKey, string> = {
    LOCATION_OF_PROPERTY_ROW: 'locationOfPropertyRow',
    COMMERCIAL_ALLOWANCE_ROW: 'commercialAllowanceRow',
    INDUSTRIAL_ALLOWANCE_ROW: 'industrialAllowanceRow',
    FURNITURE_FIXTURE_ROW: 'furnitureFixtureRow',
    COMPUTER_HARD_SOFT_ROW: 'computerHardSoftRow',
    FULL_PAY_MOTOR_ROW: 'fullPayMotorRow',
    HIRE_PURCHASE_MOTOR_ROW: 'hirePurchaseMotorRow',
    MACHINERY_ROW: 'machineryRow',
    LIFT_ESCALATOR_ROW: 'liftEscalatorRow',
    EQUIPMENT_ROW: 'equipmentRow',
};

export const PPE_CATEGORY_ROW_KEYS = Object.values(PPE_TITLE_KEY_TO_ROW_KEY) as readonly string[];

export type PpeCategoryOption = {key: string; label: string};

export const MULTILINE_PPE_TITLE_KEYS = new Set<PpeTitleKey>([
    'LOCATION_OF_PROPERTY_ROW',
    'COMMERCIAL_ALLOWANCE_ROW',
    'INDUSTRIAL_ALLOWANCE_ROW',
]);

/** Full header titles (Loc/Com/Ind are multiline). */
export const DEFAULT_PPE_HEADER_TITLES: Record<PpeTitleKey, string> = {
    LOCATION_OF_PROPERTY_ROW:
        'Leasehold land and building\n*Above is for "Commercial building allowance"\nLocation of property',
    COMMERCIAL_ALLOWANCE_ROW:
        'Leasehold improvement\n*Above is for "Commercial building allowance"\nLocation of Leasehold improvement',
    INDUSTRIAL_ALLOWANCE_ROW:
        'Leasehold improvement\n*Above is for "Industrial building allowance"\nLocation of Leasehold improvement',
    FURNITURE_FIXTURE_ROW: 'Furniture and Fixture',
    COMPUTER_HARD_SOFT_ROW: 'Computer hardware and software',
    FULL_PAY_MOTOR_ROW: '(Full pay) Motor Vehicle',
    HIRE_PURCHASE_MOTOR_ROW: '(Hire purchase) Motor Vehicle',
    MACHINERY_ROW: 'Machineries',
    LIFT_ESCALATOR_ROW: 'Lifts and escalators (electric)',
    EQUIPMENT_ROW: 'Equipments',
};

/** Short switch labels when using defaults (Loc/Com/Ind differ from header first line). */
export const DEFAULT_PPE_SWITCH_LABELS: Record<PpeTitleKey, string> = {
    LOCATION_OF_PROPERTY_ROW: 'Leasehold land and building',
    COMMERCIAL_ALLOWANCE_ROW: 'Leasehold improvement (Commercial)',
    INDUSTRIAL_ALLOWANCE_ROW: 'Leasehold improvement (Industrial)',
    FURNITURE_FIXTURE_ROW: 'Furniture and Fixture',
    COMPUTER_HARD_SOFT_ROW: 'Computer hardware and software',
    FULL_PAY_MOTOR_ROW: '(Full pay) Motor Vehicle',
    HIRE_PURCHASE_MOTOR_ROW: '(Hire purchase) Motor Vehicle',
    MACHINERY_ROW: 'Machineries',
    LIFT_ESCALATOR_ROW: 'Lifts and escalators (electric)',
    EQUIPMENT_ROW: 'Equipments',
};

export function normalizePpeTitle(value: string | null | undefined): string {
    return String(value ?? '').replace(/\r\n/g, '\n');
}

export function buildPpeTitleBaseline(
    ppeTitle?: Record<string, string> | null
): Record<PpeTitleKey, string> {
    const baseline = {...DEFAULT_PPE_HEADER_TITLES};
    if (!ppeTitle) {
        return baseline;
    }
    for (const key of PPE_TITLE_KEYS) {
        const raw = ppeTitle[key];
        if (raw != null && String(raw).trim() !== '') {
            baseline[key] = normalizePpeTitle(raw);
        }
    }
    return baseline;
}

export function resolvePpeDisplayTitles(
    ppeTitle?: Record<string, string> | null
): Record<PpeTitleKey, string> {
    return buildPpeTitleBaseline(ppeTitle);
}

export function getPpeSwitchLabel(
    key: PpeTitleKey,
    displayTitle: string
): string {
    const normalized = normalizePpeTitle(displayTitle);
    const defaultHeader = DEFAULT_PPE_HEADER_TITLES[key];
    if (normalized === defaultHeader || normalized.trim() === '') {
        return DEFAULT_PPE_SWITCH_LABELS[key];
    }
    if (MULTILINE_PPE_TITLE_KEYS.has(key)) {
        return normalized.split('\n')[0] || DEFAULT_PPE_SWITCH_LABELS[key];
    }
    return normalized;
}

/** Build category list labels from ppeTitle (custom names) or defaults. */
export function buildPpeCategoryOptions(
    ppeTitle?: Record<string, string> | null,
): PpeCategoryOption[] {
    const display = resolvePpeDisplayTitles(ppeTitle);
    return PPE_TITLE_KEYS.map((key) => ({
        key: PPE_TITLE_KEY_TO_ROW_KEY[key],
        label: getPpeSwitchLabel(key, display[key]),
    }));
}

export function renderPpeHeaderContent(title: string): React.ReactNode {
    const lines = normalizePpeTitle(title).split('\n');
    const effective = lines.length > 0 ? lines : [''];
    return (
        <>
            {effective.map((line, index) => (
                <Typography key={index} align="left">
                    {line}
                </Typography>
            ))}
        </>
    );
}

/**
 * Diff draft titles against baseline.
 * - unchanged → omit from pending
 * - empty or equal to default header → pending '' (clear)
 * - otherwise → pending new value
 */
export function diffPpeTitles(
    draft: Record<string, string>,
    baseline: Record<PpeTitleKey, string>
): {pending: Record<string, string>; display: Record<PpeTitleKey, string>} {
    const pending: Record<string, string> = {};
    const display = {...DEFAULT_PPE_HEADER_TITLES};

    for (const key of PPE_TITLE_KEYS) {
        const edited = normalizePpeTitle(draft[key]);
        const base = normalizePpeTitle(baseline[key] ?? DEFAULT_PPE_HEADER_TITLES[key]);
        const defaultHeader = DEFAULT_PPE_HEADER_TITLES[key];

        if (edited === base) {
            display[key] = edited.trim() === '' ? defaultHeader : edited;
            continue;
        }

        if (edited.trim() === '' || edited === defaultHeader) {
            pending[key] = '';
            display[key] = defaultHeader;
        } else {
            pending[key] = edited;
            display[key] = edited;
        }
    }

    return {pending, display};
}
